import {
  repositoryDelivery,
  repositoryPos,
} from "../../../../_base/_const/_constVar";
import type { MessageResponse } from "../../../../model/MessageResponse";
import type { OrderDetailOutWardNew } from "../../../../model/SaleOrderModel";
import type { TblInventorySerialOrderDetailOnHand } from "../../../../model/TblInventorySerialOnHand";
import type { TblOutwardDetail } from "../../../../model/TblOutWard";
import type { SaleSourceType } from "../config/saleKinds";
import type { CreateSaleOutwardRequest, SubmitStatus } from "../types";

const BASE = "/api/v1/Outward";
const enc = encodeURIComponent;

/** Chi nhánh dùng cho GHTK / Ahamove / Grab và hiển thị mã chi nhánh trên header */
export interface MasterOrg {
  orgId: number;
  orgCode: string;
  pick_tel: string;
  pick_address: string;
  pick_province: string;
  pick_ward: string;
  pick_address_detail: string;
}

/**
 * Mẫu tạo phiếu theo số chứng từ, đồng thời lấy mã phiếu XK mới.
 * Truyền đúng sourceType: trang cũ không truyền nên phiếu đổi trả bị backend coi là XUAT_BAN.
 */
export const getCreateTemplate = (sourceCode: string, sourceType: SaleSourceType) =>
  repositoryPos.get<MessageResponse<OrderDetailOutWardNew>>(
    `${BASE}/create-sale-order-out-ward?prefix=XK&SourceCode=${enc(sourceCode)}&sourceType=${enc(sourceType)}`,
  );

/** Chi tiết phiếu để sửa hoặc xem */
export const getOutwardDetail = (
  mode: "edit" | "detail",
  params: { id?: number | string | null; transactionCode?: string | null },
) => {
  const query = params.id
    ? `id=${enc(String(params.id))}`
    : `transactionCode=${enc(params.transactionCode ?? "")}`;
  return repositoryPos.get<MessageResponse<TblOutwardDetail>>(
    `${BASE}/${mode}-sale-order-out-ward?${query}`,
  );
};

export interface SearchSerialBody {
  serial: string;
  sourceCode: string;
  type: string;
  invId: number | null | undefined;
  positionId?: number;
}

/** Kiểm tra và lấy thông tin một serial vừa quét */
export const searchSerial = (body: SearchSerialBody) =>
  repositoryPos.post<MessageResponse<TblInventorySerialOrderDetailOnHand>>(
    "api/v1/TblItemTransaction/search-serial-item-transaction",
    { ...body, branchId: 0, isNoSerial: true },
  );

/** Kiểm tra nhiều serial một lần (import Excel) */
export const searchSerialList = (body: {
  sourceCode: string;
  type: string;
  listSerials: string[];
}) =>
  repositoryPos.post<MessageResponse<TblInventorySerialOrderDetailOnHand[]>>(
    "/api/v1/TblItemTransaction/search-list-serial-item-transaction",
    {
      KeySearch: "",
      SourceCode: body.sourceCode,
      Skip: 0,
      Take: 500,
      Type: body.type,
      ListSerials: body.listSerials,
    },
  );

/** Tìm serial tồn của các mã hàng trong đơn theo từ khoá (menu tìm sản phẩm) */
export const searchItemSerials = (params: {
  keySearch: string;
  sourceCode: string;
  type: string;
  skip: number;
  take: number;
}) =>
  repositoryPos.get<MessageResponse<any[]>>(
    `/api/v1/TblItemTransaction/search-list-serial-item-transaction?KeySearch=${enc(
      params.keySearch,
    )}&SourceCode=${enc(params.sourceCode)}&Skip=${params.skip}&Take=${params.take}&Type=${enc(
      params.type,
    )}`,
  );

export interface SaveResult {
  ok: boolean;
  /** Id phiếu backend trả về */
  transactionId: number;
  message: string;
}

/**
 * Lưu tạm (OPEN) hoặc hoàn thành (LOCK) phiếu.
 * Trả về cả thông báo lỗi của backend; trang cũ biến mọi thất bại thành null và mất thông báo.
 */
export const saveSaleOutward = async (
  request: CreateSaleOutwardRequest,
  status: SubmitStatus,
): Promise<SaveResult> => {
  const response = await repositoryPos.post<MessageResponse<any>>(
    `${BASE}/create-sale-order-out-ward`,
    { ...request, tblItemTransactionHeaderCommand: { ...request.tblItemTransactionHeaderCommand, status } },
  );
  const ok = response?.httpStatusCode === 200 && !!response?.success;
  return {
    ok,
    transactionId: ok ? Number(response?.data) || 0 : 0,
    message: response?.message ?? "",
  };
};

/** Kiểm tra FIFO (nhập trước xuất trước). success=false kèm danh sách cảnh báo */
export const checkFifo = (serials: string[], branchId: number | null, subInvId: number | null) =>
  repositoryPos.post<MessageResponse<any[]>>("/api/v1/TblInventorySerialOnHand/check-fifo", {
    listSerial: serials,
    branchId,
    subInvId,
  });

/** Danh sách chi nhánh kèm thông tin điểm lấy hàng */
export const getMasterOrgs = async (): Promise<MasterOrg[]> => {
  const res = await repositoryDelivery.get<MessageResponse<any[]>>("/api/v1/TblDmMasterOrg/get-all");
  if (!res?.success || !Array.isArray(res.data)) return [];
  return res.data.map((i: any) => ({
    orgId: i.id,
    orgCode: i.code,
    pick_tel: i?.attribute11 ?? "",
    pick_address: i?.address ?? "",
    pick_province: i?.attribute8 ?? "",
    pick_ward: i?.attribute9 ?? "",
    pick_address_detail: i?.attribute10 ?? "",
  }));
};

/** Linh kiện lắp ráp của một serial (in LKLR) */
export const getSerialParentInfo = (serial: string) =>
  repositoryPos.post<MessageResponse<any[]>>("/api/v1/TblItemTransaction/get-serial-parent-info", {
    parentSerial: [serial],
  });
